import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React from 'react';
import { FormattedNumber } from 'react-intl';
import VisibilitySensor from 'react-visibility-sensor';

import type { RowProps } from '@swf/casino';
import { PrizeCurrency } from '@swf/casino';
import Translation from '@swf/components/Translation/Translation';
import bronzeTrophy from '@swf/core/assets/casino/bronze-trophy.png';
import goldTrophy from '@swf/core/assets/casino/gold-trophy.png';
import silverTrophy from '@swf/core/assets/casino/silver-trophy.png';
import Medal from '@swf/core/assets/icons/medal.svg';
import Laurel from '@swf/core/assets/icons/wreath.svg';
import type { Standing } from '@swf/core/types/CasinoRaces';

import { TXTMOB_You } from './LeaderboardRow.messages.json';
import styles from './LeaderboardRow.scss';

const PlaceClass = {
  1: styles.first,
  2: styles.second,
  3: styles.third
};

const prize = (standing: Standing) => {
  if (standing.rank === 1) {
    return (
      <div className={styles.winner}>
        <span className={styles.laurel1}>
          <Laurel />
        </span>
        <PrizeCurrency {...standing} />
        <span className={styles.laurel2}>
          <Laurel />
        </span>
      </div>
    );
  } else {
    return (
      <span className={styles.prize}>
        {!isEmpty(standing) ? <PrizeCurrency {...standing} /> : '–'}
      </span>
    );
  }
};

const trophy = (rank: number) => {
  switch (rank) {
    case 1:
      return <img className={styles.goldTrophy} src={goldTrophy} alt="" />;
    case 2:
      return <img className={styles.silverTrophy} src={silverTrophy} alt="" />;
    case 3:
      return <img className={styles.bronzeTrophy} src={bronzeTrophy} alt="" />;
    default:
      return <Medal />;
  }
};

const Rank: React.FC<RowProps> = ({
  standing,
  userStanding,
  floatingYou,
  onYouVisible,
  containment
}) => {
  switch (true) {
    case userStanding?.rank === standing.rank && floatingYou:
      return <Translation {...TXTMOB_You} />;
    case userStanding?.rank === standing.rank:
      return (
        <VisibilitySensor onChange={onYouVisible} partialVisibility containment={containment}>
          <Translation tagName="span" {...TXTMOB_You} />
        </VisibilitySensor>
      );
    default:
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <>{!!standing.name && standing.name}</>;
  }
};

const LeaderboardRow: React.FC<RowProps> = (props) => {
  const {
    index,
    standing,
    isMobile = false,
    floatingYou = false,
    medal = false,
    leaderboard = false,
    grouped = false,
    onClick
  } = props;
  const userPrize = standing.userRacePrize;
  const rankNum = '#' + standing.rank + '  ';
  return (
    <tr
      key={index}
      className={classNames(styles.legacy, styles.row, PlaceClass[standing.rank] || '', {
        [styles.floatingYou]: floatingYou,
        [styles.paid]: !isEmpty(userPrize) && userPrize.amount > 0,
        [styles.medal]: medal,
        [styles.mobile]: isMobile,
        [styles.grouped]: grouped
      })}
      onClick={onClick}
    >
      <td className={classNames({ [styles.mobile]: isMobile })}>
        {
          <div className={classNames({ [styles.trophy]: standing.rank < 4 })}>
            {isMobile ? null : trophy(standing.rank)}
          </div>
        }
      </td>

      <td>
        <span className={styles.rank}>
          {rankNum}
          <Rank {...props} />
        </span>
      </td>
      {leaderboard && (
        <td>
          {standing.score > 0 ? (
            <FormattedNumber
              value={standing.score}
              maximumFractionDigits={0}
              minimumFractionDigits={0}
            />
          ) : (
            '–'
          )}
        </td>
      )}
      <td>{prize({ ...userPrize, rank: standing.rank } as Standing)}</td>
    </tr>
  );
};

export default LeaderboardRow;
